import ComparatorBar from "@/components/ComparatorBar";
import NumericInput from "@/components/NumericInput";
import type { ParamMeta } from "@/lib/params";

interface Props {
  meta: ParamMeta;
  value: string;
  onChange: (next: string) => void;
  ideal?: { low: number; high: number } | null;
}

/**
 * One measurement input with its comparator underneath.
 *
 * The bar updates as you type, so you see where the reading lands *before* you
 * save it — the same feedback you get holding a vial against the comparator
 * block, and the moment when knowing matters most.
 */
export default function MeasurementField({ meta, value, onChange, ideal = null }: Props) {
  const parsed = value.trim() === "" ? null : Number(value);
  const numeric = parsed !== null && Number.isFinite(parsed) ? parsed : null;

  return (
    <div className="py-3">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={`field-${meta.key}`} className="text-sm font-medium">
          {meta.full}
        </label>
        <span className="unit">{meta.unit || meta.label}</span>
      </div>

      <div className="mt-2 flex items-center gap-3">
        <NumericInput
          id={`field-${meta.key}`}
          value={value}
          onChange={onChange}
          placeholder="—"
          className="field figure flex-1 text-2xl"
          aria-describedby={meta.hint ? `hint-${meta.key}` : undefined}
        />
      </div>

      <div className="mt-2">
        <ComparatorBar
          value={numeric}
          scaleMin={meta.scaleMin}
          scaleMax={meta.scaleMax}
          ideal={ideal}
          color={meta.color}
          label={meta.full}
          size="sm"
        />
        <div className="mt-1 flex justify-between">
          <span className="unit">{meta.scaleMin}</span>
          {meta.hint && (
            <span id={`hint-${meta.key}`} className="px-2 text-center text-xs text-(--color-ink-faint)">
              {meta.hint}
            </span>
          )}
          <span className="unit">{meta.scaleMax}</span>
        </div>
      </div>
    </div>
  );
}
